import React from "react";
import { Link } from "react-router-dom";
import { BRAND, WHY_US, STORE_STATS } from "../mock/mock";
import { Award, BadgeCheck, Gift, Leaf, Package, ShieldCheck, Sprout, Truck, ArrowRight, Home } from "lucide-react";

const iconMap = { Sprout, ShieldCheck, Package, BadgeCheck, Truck, Gift, Award, Leaf };

const AboutPage = () => {
  return (
    <main className="bg-cream">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/5254831/pexels-photo-5254831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2a1a15]/85 via-[#2a1a15]/60 to-[#2a1a15]/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-16 md:py-24 text-white">
          <div className="flex items-center gap-2 text-[12.5px] text-[#f2e7d1]">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-gold"><Home className="h-3.5 w-3.5" /> Home</Link>
            <span>/</span><span className="text-gold">Our Story</span>
          </div>
          <div className="text-gold text-[11px] tracking-[0.28em] font-bold mt-4">SINCE 1973</div>
          <h1 className="mt-2 font-serif-brand text-[40px] md:text-[60px] font-bold leading-tight max-w-2xl">A Legacy of Goodness</h1>
          <p className="mt-4 text-[16px] text-[#f2e7d1] max-w-xl">From a humble beginning in 1973 to becoming a trusted name in premium dry fruits — our journey is built on quality, trust and tradition.</p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
        <img src="https://images.unsplash.com/photo-1722886705787-4b8d676309fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkcnklMjBmcnVpdHMlMjB3b29kZW4lMjB0YWJsZXxlbnwwfHx8fDE3ODQ0NjUwNjl8MA&ixlib=rb-4.1.0&q=85" alt="" className="rounded-3xl w-full h-[440px] object-cover" />
        <div>
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-2">OUR JOURNEY</div>
          <h2 className="font-serif-brand text-[30px] md:text-[40px] font-bold text-[#2a1a15] leading-tight">Goodness that stays, relationships that last</h2>
          <p className="mt-4 text-[15px] text-[#3a2a20] leading-relaxed">What began as a small family business over five decades ago has grown into a trusted premium dry fruits brand serving families and businesses across India. Today, under the leadership of Nitin Agrawal, Agrawal Rich Dry Fruits carries forward its founder's promise — uncompromised quality, honest sourcing, and warm service.</p>
          <p className="mt-4 text-[15px] text-[#3a2a20] leading-relaxed">Every almond we sell, every hamper we craft, and every relationship we nurture is rooted in our founding values — <b>quality, trust and tradition.</b></p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {STORE_STATS.map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-[#efe4c9] p-4">
                <div className="font-serif-brand text-2xl md:text-3xl font-bold text-brand">{s.count}</div>
                <div className="text-[13px] text-[#6b4f39]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#f3ecda] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.28em] font-bold text-olive">OUR PILLARS</div>
            <h2 className="font-serif-brand text-[28px] md:text-[40px] font-bold text-[#2a1a15] leading-tight mt-1">Built on four unshakeable values</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { title: "Legacy", desc: "A 50+ year journey, now proudly taken forward by Nitin Agrawal.", icon: Award },
              { title: "Premium Quality", desc: "Only the finest nuts and dry fruits make it into our packs.", icon: BadgeCheck },
              { title: "Carefully Sourced", desc: "Direct partnerships with trusted growers across the globe.", icon: Sprout },
              { title: "Commitment to Excellence", desc: "Every batch, every pack — nothing short of exceptional.", icon: ShieldCheck },
            ].map((p) => (
              <div key={p.title} className="bg-white rounded-2xl p-6 border border-[#efe4c9]">
                <p.icon className="h-8 w-8 text-brand" />
                <h3 className="font-serif-brand text-xl font-bold text-[#2a1a15] mt-3">{p.title}</h3>
                <p className="text-[13.5px] text-[#6b4f39] mt-1.5">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive">WHY AGRAWAL RICH</div>
          <h2 className="font-serif-brand text-[28px] md:text-[40px] font-bold text-[#2a1a15] leading-tight mt-1">More than dry fruits — a promise of goodness</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {WHY_US.map((w) => {
            const I = iconMap[w.icon] || Leaf;
            return (
              <div key={w.title} className="bg-white rounded-2xl border border-[#efe4c9] p-5">
                <div className="h-11 w-11 rounded-full bg-[#f9f1de] flex items-center justify-center"><I className="h-5 w-5 text-brand" /></div>
                <h3 className="font-serif-brand text-lg font-bold text-[#2a1a15] mt-3">{w.title}</h3>
                <p className="text-[13px] text-[#6b4f39] mt-1.5">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 pb-16">
        <div className="rounded-3xl bg-[#2a1a15] text-white p-8 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-gold text-[11px] tracking-[0.28em] font-bold">EXPERIENCE THE LEGACY</div>
            <h3 className="font-serif-brand text-[26px] md:text-[36px] font-bold mt-2">Taste 50 years of trust in every bite.</h3>
          </div>
          <div className="flex gap-3">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold px-6 py-3 rounded-full transition-colors">Shop Now <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white hover:text-[#2a1a15] font-medium px-6 py-3 rounded-full transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
